import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { cn } from "../../../utils";
import { Button, buttonVariants } from "../button/button";
const defaultClassNames = getDefaultClassNames();
/*
 * DEVIATIONS from the shadcn `base-vega` registry source, all of them
 * deliberate — a diff against upstream is expected to show exactly these:
 *
 * - Imports are relative (`../../../utils`, `../button/button`) instead of the
 *   registry's `@/` aliases, which this package does not define.
 * - The registry's leading `"use client"` is dropped: this package ships no
 *   React Server Components, so the directive would name a boundary that does
 *   not exist here.
 * - The chevrons are `lucide-react` icons rather than the registry's
 *   `IconPlaceholder`, which is a scaffolding stub.
 * - `CalendarWeekNumber` destructures `week` away before spreading. It is
 *   react-day-picker's own week object, and left in the rest React writes a
 *   literal `week="[object Object]"` onto the `td`.
 * - `defaultClassNames` is read once at module scope instead of per render:
 *   `getDefaultClassNames()` answers the same constant every time.
 * - The registry's `cn-*` marker classes are stripped, and `dropdown_root`
 *   gains a `has-focus` ring: the dropdown is a transparent native select, so
 *   without it its keyboard focus is invisible.
 * - `ref={ref}` is attached on `CalendarDayButton`. Upstream creates the ref
 *   and focuses through it but never puts it on the element, which leaves
 *   keyboard navigation with no focus at all.
 * - The week-number column is styled apart from the day grid (smaller, fainter,
 *   tabular digits, a separator border): with the registry styling the week
 *   numbers read as a column of days.
 * - `formatMonthDropdown` falls back to `en-US` rather than to the browser
 *   locale, so an unlocalized calendar reads English throughout instead of
 *   mixing a browser-language month list into English weekday names.
 * - The three components below are hoisted and the three objects under them
 *   memoized: react-day-picker keys its internal config memo on the identity of
 *   `formatters`, `classNames` and `components`, and a `Root` minted per render
 *   is a new component type, which remounts the whole calendar DOM subtree —
 *   losing focus mid-keyboard-navigation.
 */ function CalendarRoot({ className, rootRef, ...props }) {
    return /*#__PURE__*/ _jsx("div", {
        "data-slot": "calendar",
        ref: rootRef,
        className: cn(className),
        ...props
    });
}
function CalendarChevron({ className, orientation, ...props }) {
    if (orientation === "left") {
        return /*#__PURE__*/ _jsx(ChevronLeftIcon, {
            className: cn("size-4", className),
            ...props
        });
    }
    if (orientation === "right") {
        return /*#__PURE__*/ _jsx(ChevronRightIcon, {
            className: cn("size-4", className),
            ...props
        });
    }
    return /*#__PURE__*/ _jsx(ChevronDownIcon, {
        className: cn("size-4", className),
        ...props
    });
}
// `week` is react-day-picker's own week object: its default WeekNumber drops it
// before spreading, and this td override has to do the same or React writes a
// literal week="[object Object]" attribute.
function CalendarWeekNumber({ children, week, ...props }) {
    return /*#__PURE__*/ _jsx("td", {
        ...props,
        children: /*#__PURE__*/ _jsx("div", {
            className: "flex size-(--cell-size) items-center justify-center text-center",
            children: children
        })
    });
}
function Calendar({ className, classNames, showOutsideDays = true, captionLayout = "label", buttonVariant = "ghost", locale, formatters, components, ...props }) {
    const showWeekNumber = props.showWeekNumber;
    const mergedFormatters = React.useMemo(()=>({
            formatMonthDropdown: (date)=>date.toLocaleString(locale?.code ?? "en-US", {
                    month: "short"
                }),
            ...formatters
        }), [
        locale,
        formatters
    ]);
    const mergedClassNames = React.useMemo(()=>({
            root: cn("w-fit", defaultClassNames.root),
            months: cn("relative flex flex-col gap-4 md:flex-row", defaultClassNames.months),
            month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
            nav: cn("absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1", defaultClassNames.nav),
            button_previous: cn(buttonVariants({
                variant: buttonVariant
            }), "size-(--cell-size) p-0 select-none aria-disabled:opacity-50", defaultClassNames.button_previous),
            button_next: cn(buttonVariants({
                variant: buttonVariant
            }), "size-(--cell-size) p-0 select-none aria-disabled:opacity-50", defaultClassNames.button_next),
            month_caption: cn("flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)", defaultClassNames.month_caption),
            dropdowns: cn("flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium", defaultClassNames.dropdowns),
            // The dropdown itself is a transparent native select, so without this
            // ring its keyboard focus is invisible.
            dropdown_root: cn("relative rounded-(--cell-radius) has-focus:ring-[3px] has-focus:ring-ring/50", defaultClassNames.dropdown_root),
            dropdown: cn("absolute inset-0 bg-popover opacity-0", defaultClassNames.dropdown),
            caption_label: cn("font-medium select-none", captionLayout === "label" ? "text-sm" : "flex items-center gap-1 rounded-(--cell-radius) text-sm [&>svg]:size-3.5 [&>svg]:text-muted-foreground", defaultClassNames.caption_label),
            month_grid: cn("w-full border-collapse", defaultClassNames.month_grid),
            weekdays: cn("flex", defaultClassNames.weekdays),
            weekday: cn("flex-1 rounded-(--cell-radius) text-[0.8rem] font-normal text-muted-foreground select-none", defaultClassNames.weekday),
            week: cn("mt-2 flex w-full", defaultClassNames.week),
            week_number_header: cn("w-(--cell-size) border-r border-border text-[0.65rem] font-normal text-muted-foreground/70 select-none", defaultClassNames.week_number_header),
            week_number: cn("border-r border-border text-[0.65rem] text-muted-foreground/70 tabular-nums select-none", defaultClassNames.week_number),
            day: cn("group/day relative aspect-square h-full w-full rounded-(--cell-radius) p-0 text-center select-none [&:last-child[data-selected=true]_button]:rounded-r-(--cell-radius)", showWeekNumber ? "[&:nth-child(2)[data-selected=true]_button]:rounded-l-(--cell-radius)" : "[&:first-child[data-selected=true]_button]:rounded-l-(--cell-radius)", defaultClassNames.day),
            range_start: cn("relative isolate z-0 rounded-l-(--cell-radius) bg-muted after:absolute after:inset-y-0 after:right-0 after:w-4 after:bg-muted", defaultClassNames.range_start),
            range_middle: cn("rounded-none", defaultClassNames.range_middle),
            range_end: cn("relative isolate z-0 rounded-r-(--cell-radius) bg-muted after:absolute after:inset-y-0 after:left-0 after:w-4 after:bg-muted", defaultClassNames.range_end),
            today: cn("rounded-(--cell-radius) bg-muted text-foreground data-[selected=true]:rounded-none", defaultClassNames.today),
            outside: cn("text-muted-foreground aria-selected:text-muted-foreground", defaultClassNames.outside),
            disabled: cn("text-muted-foreground opacity-50", defaultClassNames.disabled),
            hidden: cn("invisible", defaultClassNames.hidden),
            ...classNames
        }), [
        buttonVariant,
        captionLayout,
        classNames,
        showWeekNumber
    ]);
    const mergedComponents = React.useMemo(()=>({
            Root: CalendarRoot,
            Chevron: CalendarChevron,
            DayButton: (dayButtonProps)=>/*#__PURE__*/ _jsx(CalendarDayButton, {
                    locale: locale,
                    ...dayButtonProps
                }),
            WeekNumber: CalendarWeekNumber,
            ...components
        }), [
        components,
        locale
    ]);
    return /*#__PURE__*/ _jsx(DayPicker, {
        showOutsideDays: showOutsideDays,
        className: cn("group/calendar bg-background p-3 [--cell-radius:var(--radius-md)] [--cell-size:--spacing(8)] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent", String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`, String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`, className),
        captionLayout: captionLayout,
        locale: locale,
        formatters: mergedFormatters,
        classNames: mergedClassNames,
        components: mergedComponents,
        ...props
    });
}
function CalendarDayButton({ className, day, modifiers, locale, ...props }) {
    const ref = React.useRef(null);
    React.useEffect(()=>{
        if (modifiers.focused) ref.current?.focus();
    }, [
        modifiers.focused
    ]);
    return /*#__PURE__*/ _jsx(Button, {
        ref: ref,
        variant: "ghost",
        size: "icon",
        "data-day": day.date.toLocaleDateString(locale?.code),
        "data-selected-single": modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle,
        "data-range-start": modifiers.range_start,
        "data-range-end": modifiers.range_end,
        "data-range-middle": modifiers.range_middle,
        className: cn("relative isolate z-10 flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 border-0 leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-ring/50 data-[range-end=true]:rounded-(--cell-radius) data-[range-end=true]:rounded-r-(--cell-radius) data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-muted data-[range-middle=true]:text-foreground data-[range-start=true]:rounded-(--cell-radius) data-[range-start=true]:rounded-l-(--cell-radius) data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground dark:hover:text-foreground [&>span]:text-xs [&>span]:opacity-70", defaultClassNames.day, className),
        ...props
    });
}
export { Calendar, CalendarDayButton };

//# sourceMappingURL=calendar.js.map